import { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import Handlebars from 'handlebars';
import useBridge from './use-bridge-hook';
import {
  CustomerObject,
  IFlowBridge,
  InteractionObject,
  Target,
  TargetLineage,
} from './flow-bridge';

function calculateVenueAndEmployment(profiles: Target[]) {
  const employee = profiles?.find((p) => p.type === 'profile');
  const venue = profiles?.find((p) => p.type === 'organization-unit');
  return {
    employee,
    venue,
    profile: employee, // in lineage.targets, there is employee/profile, venue/unit based on structureType
    organizationUnit: venue,
  };
}

async function calculateView(
  bridge: IFlowBridge,
): Promise<Record<string, unknown>> {
  const [customers, interactions, lineages] = await Promise.all([
    bridge.load({ objectType: 'customer' }),
    bridge.load({ objectType: 'interaction' }),
    bridge.load({ objectType: 'target-lineage' }),
  ]);
  const customer = customers?.[0] as CustomerObject;
  const interaction = interactions?.[0] as InteractionObject;
  const lineage = lineages?.[0] as TargetLineage;
  const profiles = lineage.targets;
  const { venue, employee } = calculateVenueAndEmployment(profiles);
  return {
    name: customer?.name,
    salutation: customer?.salutation,
    male: customer?.gender ? customer.gender.toLowerCase() === 'm' : false,
    female: customer?.gender ? customer.gender.toLowerCase() === 'f' : false,
    unknown_gender:
      !(customer?.gender ? customer?.gender.toLowerCase() === 'm' : false) &&
      !(customer?.gender ? customer?.gender.toLowerCase() === 'f' : false),
    employee_name: employee?.name,
    employee_description: employee?.description,
    employee_image: employee?.imageUrl,

    profile_name: employee?.name,
    profile_category: employee?.description,
    profile_image: employee?.imageUrl,

    venue_name: venue?.name,
    venue_description: venue?.description,
    venue_image: venue?.imageUrl,

    organization_unit_name: venue?.name,
    organization_unit_category: venue?.description,
    organization_unit_image: venue?.imageUrl,

    organization_unit_attribute_rating_url: venue?.attributes?.ratingUrl,

    customer_email: customer?.email,
    customer_attribute_1: customer?.attribute1,
    customer_attribute_2: customer?.attribute2,
    customer_attribute_3: customer?.attribute3,
    customer_attribute_4: customer?.attribute4,
    customer_attribute_5: customer?.attribute5,
    customer_attribute_6: customer?.attribute6,
    customer_attribute_7: customer?.attribute7,
    customer_attribute_8: customer?.attribute8,
    customer_attribute_9: customer?.attribute9,
    customer_attribute_10: customer?.attribute10,
    customer_attribute_11: customer?.attribute11,
    customer_attribute_12: customer?.attribute12,
    customer_attribute_13: customer?.attribute13,
    customer_attribute_14: customer?.attribute14,
    customer_attribute_15: customer?.attribute15,
    customer_attribute_16: customer?.attribute16,
    customer_attribute_17: customer?.attribute17,
    customer_attribute_18: customer?.attribute18,
    customer_attribute_19: customer?.attribute19,
    customer_attribute_20: customer?.attribute20,
    interaction_attribute_1: interaction?.attribute1,
    interaction_attribute_2: interaction?.attribute2,
    interaction_attribute_3: interaction?.attribute3,
    interaction_attribute_4: interaction?.attribute4,
    interaction_attribute_5: interaction?.attribute5,
    interaction_attribute_6: interaction?.attribute6,
    interaction_attribute_7: interaction?.attribute7,
    interaction_attribute_8: interaction?.attribute8,
    interaction_attribute_9: interaction?.attribute9,
    interaction_attribute_10: interaction?.attribute10,
    interaction_attribute_11: interaction?.attribute11,
    interaction_attribute_12: interaction?.attribute12,
    interaction_attribute_13: interaction?.attribute13,
    interaction_attribute_14: interaction?.attribute14,
    interaction_attribute_15: interaction?.attribute15,
    interaction_attribute_16: interaction?.attribute16,
    interaction_attribute_17: interaction?.attribute17,
    interaction_attribute_18: interaction?.attribute18,
    interaction_attribute_19: interaction?.attribute19,
    interaction_attribute_20: interaction?.attribute20,
    interaction_attribute_21: interaction?.attribute21,
    interaction_attribute_22: interaction?.attribute22,
    interaction_attribute_23: interaction?.attribute23,
    interaction_attribute_24: interaction?.attribute24,
    interaction_attribute_25: interaction?.attribute25,
    interaction_attribute_26: interaction?.attribute26,
    interaction_attribute_27: interaction?.attribute27,
    interaction_attribute_28: interaction?.attribute28,
    interaction_attribute_29: interaction?.attribute29,
    interaction_attribute_30: interaction?.attribute30,
    interaction_attribute_31: interaction?.attribute31,
    interaction_attribute_32: interaction?.attribute32,
    interaction_attribute_33: interaction?.attribute33,
    interaction_attribute_34: interaction?.attribute34,
    interaction_attribute_35: interaction?.attribute35,
    interaction_attribute_36: interaction?.attribute36,
    interaction_attribute_37: interaction?.attribute37,
    interaction_attribute_38: interaction?.attribute38,
    interaction_attribute_39: interaction?.attribute39,
    interaction_attribute_40: interaction?.attribute40,
    interaction_attribute_41: interaction?.attribute41,
    interaction_attribute_42: interaction?.attribute42,
    interaction_attribute_43: interaction?.attribute43,
    interaction_attribute_44: interaction?.attribute44,
    interaction_attribute_45: interaction?.attribute45,
    interaction_attribute_46: interaction?.attribute46,
    interaction_attribute_47: interaction?.attribute47,
    interaction_attribute_48: interaction?.attribute48,
    interaction_attribute_49: interaction?.attribute49,
    interaction_attribute_50: interaction?.attribute50,
    interaction_attribute_51: interaction?.attribute51,
    interaction_attribute_52: interaction?.attribute52,
    interaction_attribute_53: interaction?.attribute53,
    interaction_attribute_54: interaction?.attribute54,
    interaction_attribute_55: interaction?.attribute55,
  };
}

export default function useTemplateRenderer(...templates: string[]): string[] {
  const bridge = useBridge();
  const [renderedTemplates, setRenderedTemplates] = useState(
    templates.map(() => null),
  );
  useDeepCompareEffect(() => {
    calculateView(bridge)
      .then((view) =>
        Promise.all(
          templates.map((template) =>
            template ? Handlebars.compile(template)(view) : template,
          ),
        ),
      )
      .then(setRenderedTemplates);
  }, [bridge, templates]);

  return renderedTemplates;
}

export function useTemplateRendererNoEscape(...templates: string[]): string[] {
  const bridge = useBridge();
  const [renderedTemplates, setRenderedTemplates] = useState(
    templates.map(() => null),
  );
  useDeepCompareEffect(() => {
    calculateView(bridge)
      .then((view) =>
        Promise.all(
          templates.map((template) =>
            template
              ? Handlebars.compile(template, { noEscape: true })(view)
              : template,
          ),
        ),
      )
      .then(setRenderedTemplates);
  }, [bridge, templates]);

  return renderedTemplates;
}
